import React from "react";
import { FileDownloadOutlined } from "@mui/icons-material";
import { convertUtcToUae, formatDateToDDMMYYYY } from "@utils/date-time";
import { useTranslation } from "react-i18next";

interface DocumentCardProps {
  "title"?: string;
  "documentName"?: string | null;
  "documentType"?: string | null;
  "issueDate"?: string;
  "norTime"?: string;
  "expiryDate"?: string;
  "updateDate"?: string | null;
  "otherSpecify"?: string;
  "documentSummary"?: string;
  "onDownload"?: () => void;
  "onPreview"?: () => void;
  "testId"?: string;
  "data-testid"?: string;
}

const DOCUMENTS_TRANSLATION_BASE = "vessel-nomination.nomination-details.documents";

const DocumentCard: React.FC<DocumentCardProps> = ({
  title,
  documentName,
  documentType,
  issueDate,
  norTime,
  expiryDate,
  updateDate,
  otherSpecify,
  documentSummary,
  onDownload,
  onPreview,
  testId,
  "data-testid": dataTestId,
}) => {
  const { t } = useTranslation();

  const translatedTitle = title ? t(`${DOCUMENTS_TRANSLATION_BASE}.slot.${title}`) : undefined;

  const translatedDocumentType = documentType
    ? t(`${DOCUMENTS_TRANSLATION_BASE}.type.${documentType}`)
    : undefined;

  return (
    <div data-testid={dataTestId ?? testId} className="border rounded p-4 w-full border-[#F1F2F4]">
      {title && <h3 className="font-bold text-base mb-2">{translatedTitle}</h3>}

      <div className="flex justify-start items-center mb-2 w-full">
        <a
          href="#"
          className="text-blue-600 underline break-all cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            onPreview?.();
          }}
        >
          {documentName || "-"}
        </a>

        {onDownload && (
          <button
            type="button"
            onClick={onDownload}
            className="cursor-pointer text-left ml-auto"
            title={t("common.download")}
          >
            <FileDownloadOutlined />
          </button>
        )}
      </div>

      <p className="text-sm text-[#646A77]">
        {documentType && (
          <span className="block">
            {t(`${DOCUMENTS_TRANSLATION_BASE}.document-type`)}
            {translatedDocumentType}
          </span>
        )}

        {otherSpecify && (
          <span className="block">
            {t(`${DOCUMENTS_TRANSLATION_BASE}.other-specify`)}
            {otherSpecify}
          </span>
        )}

        {documentSummary && (
          <span className="block">
            {t(`${DOCUMENTS_TRANSLATION_BASE}.document-summary`)}
            {documentSummary}
          </span>
        )}

        {norTime && (
          <span className="block">
            {t(`${DOCUMENTS_TRANSLATION_BASE}.nor-time`)}
            {formatDateToDDMMYYYY(convertUtcToUae(norTime), true)}
          </span>
        )}

        {issueDate && (
          <span className="block">
            {t(`${DOCUMENTS_TRANSLATION_BASE}.issue-date`)}
            {formatDateToDDMMYYYY(issueDate)}
          </span>
        )}

        {expiryDate && (
          <span className="block">
            {t(`${DOCUMENTS_TRANSLATION_BASE}.expiry-date`)}
            {formatDateToDDMMYYYY(expiryDate)}
          </span>
        )}

        {updateDate && (
          <span className="block">
            {t(`${DOCUMENTS_TRANSLATION_BASE}.update-date`)}
            {formatDateToDDMMYYYY(convertUtcToUae(updateDate), true)}
          </span>
        )}
      </p>
    </div>
  );
};

export default DocumentCard;
